/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import "./styles.css";

const AutoPlayCarousel = ({
  children,
  show,
  initialIndex = 0,
  infiniteLoop,
  autoplay = false,
  showControls = true,
}) => {
  // Sin loop infinito el índice no puede pasar de `length - show`: si arranca más
  // allá (por ejemplo, pidiendo el último ítem con varios visibles) el carrusel
  // queda mostrando una sola tarjeta y el resto del ancho vacío.
  const clampIndex = (index) =>
    Math.max(0, Math.min(index, children.length - show));

  const [currentIndex, setCurrentIndex] = useState(
    infiniteLoop ? show : clampIndex(initialIndex)
  );

  const [length, setLength] = useState(children.length);

  const [isRepeating, setIsRepeating] = useState(
    infiniteLoop && children.length > show
  );

  const [transitionEnabled, setTransitionEnabled] = useState(true);

  const [touchPosition, setTouchPosition] = useState(null);

  const autoPlayDelay = Math.floor(Math.random() * 2000) + 2000;

  useEffect(() => {
    setLength(children.length);
    setIsRepeating(infiniteLoop && children.length > show);
  }, [children, infiniteLoop, show]);

  // Al cambiar la cantidad visible (resize) el índice actual puede quedar fuera
  // de rango; se vuelve a acotar.
  useEffect(() => {
    if (!infiniteLoop) {
      setCurrentIndex((prev) => clampIndex(prev));
    }
  }, [children.length, infiniteLoop, show]);

  useEffect(() => {
    if (isRepeating) {
      if (currentIndex === show || currentIndex === length) {
        setTransitionEnabled(true);
      }
    }
  }, [currentIndex, isRepeating, show, length]);

  const next = useCallback(() => {
    if (isRepeating || currentIndex < length - show) {
      setCurrentIndex((prevState) => prevState + 1);
    }
  }, [currentIndex, isRepeating, length, show]);

  const prev = useCallback(() => {
    if (isRepeating || currentIndex > 0) {
      setCurrentIndex((prevState) => prevState - 1);
    }
  }, [currentIndex, isRepeating]);

  const handleTouchStart = (e) => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  };

  const handleTouchMove = (e) => {
    const touchDown = touchPosition;

    if (touchDown === null) {
      return;
    }

    const currentTouch = e.touches[0].clientX;
    const diff = touchDown - currentTouch;

    if (diff > 5) {
      next();
    }

    if (diff < -5) {
      prev();
    }

    setTouchPosition(null);
  };

  const handleTransitionEnd = () => {
    if (isRepeating) {
      if (currentIndex === 0) {
        setTransitionEnabled(false);
        setCurrentIndex(length);
      } else if (currentIndex >= length + show) {
        setTransitionEnabled(false);
        setCurrentIndex(show);
      }
    }
  };

  const renderExtraPrev = () => {
    let output = [];
    for (let index = 0; index < show; index++) {
      output.push(children[length - 1 - index]);
    }
    output.reverse();
    return output;
  };

  const renderExtraNext = () => {
    let output = [];
    for (let index = 0; index < show; index++) {
      output.push(children[index]);
    }
    return output;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(next, autoPlayDelay);
      return () => {
        clearInterval(interval);
      };
    }
  }, []);

  return (
    <div className="carousel-container">
      <div className="carousel-wrapper">
        {showControls && (isRepeating || currentIndex > 0) && (
          <button onClick={prev} className="left-arrow">
            &lt;
          </button>
        )}
        <div
          className="carousel-content-wrapper"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        >
          <div
            className={`carousel-content show-${length > show ? show : length}`}
            style={{
              transform: `translateX(-${
                show >= length ? 0 : currentIndex * (100 / show)
              }%)`,
              transition: !transitionEnabled ? "none" : undefined,
            }}
            onTransitionEnd={() => handleTransitionEnd()}
          >
            {length > show && isRepeating && renderExtraPrev()}
            {children}
            {length > show && isRepeating && renderExtraNext()}
          </div>
        </div>
        {showControls && (isRepeating || currentIndex < length - show) && (
          <button onClick={next} className="right-arrow">
            &gt;
          </button>
        )}
      </div>
    </div>
  );
};

export default AutoPlayCarousel;
